/**
 * Copyright Highway9 Networks Inc.
 *
 * Client for the scheduled task API exposed by saas-api at
 * /api/v1/saas/scheduledTasks. Scheduled tasks run a Ziva prompt on a cron
 * schedule (minute accuracy). The list returned by GET is scoped to the
 * signed-in user and their team.
 */

import { authenticationService } from "./authentication-service";

const REST_BASE = "/api/v1/saas/scheduledTasks";

export type ScheduledTaskExecution = {
  sessionId?: string;
  executedAt?: number;
};

export type ScheduledTaskRuntimeInfo = {
  lastExecutedAt?: number;
  nextRunAt?: number;
  executions?: ScheduledTaskExecution[];
};

export type ScheduledTask = {
  id?: string;
  name?: string;
  prompt?: string;
  /** Canonical Ziva model id (`provider/model`). Empty uses the Ziva UI default. */
  model?: string;
  cronSchedule?: string;
  timezone?: string;
  status?: "Scheduled" | "Expired" | "Disabled";
  enabled?: boolean;
  startAt?: number;
  endAt?: number;
  creationTime?: number;
  updationTime?: number;
  user?: { id?: string; name?: string };
  runtimeInfo?: ScheduledTaskRuntimeInfo;
};

const authHeader = () => ({
  Authorization: `Bearer ${authenticationService?.currentUserValue?.token ?? ""}`,
});

export const scheduledTaskService = {
  async list(): Promise<ScheduledTask[]> {
    const r = await fetch(REST_BASE, { headers: { ...authHeader() } });
    if (!r.ok) {
      throw new Error(`Failed to load scheduled tasks (${r.status})`);
    }
    // An empty list may come back as an empty body; treat that as no tasks
    // rather than a parse error.
    const text = await r.text();
    if (!text.trim()) {
      return [];
    }
    try {
      const body = JSON.parse(text) as { data?: ScheduledTask[] };
      return body?.data ?? [];
    } catch {
      return [];
    }
  },

  async create(task: ScheduledTask): Promise<ScheduledTask> {
    const r = await fetch(REST_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(task),
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "create scheduled task"));
    }
    return (await r.json()) as ScheduledTask;
  },

  async update(id: string, task: ScheduledTask): Promise<void> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(task),
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "update scheduled task"));
    }
  },

  async remove(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    if (!r.ok) {
      throw new Error(await extractError(r, "delete scheduled task"));
    }
  },
};

async function extractError(r: Response, action: string): Promise<string> {
  try {
    const body = (await r.json()) as {
      errors?: { message?: string }[];
    };
    const msg = body?.errors?.[0]?.message;
    if (msg) return msg;
  } catch {
    // ignore parse errors
  }
  return `Failed to ${action} (${r.status})`;
}
